import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { validateConfigurazione, hasErrors } from '@/utils/validation'
import {
  ArrowLeftIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { Package } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { ChevronsUpDown } from 'lucide-react'
import { configurazioniApi, componentiApi } from '@/api/box'
import { fetchAllPaginatedResults } from '@/utils/pagination'

/**
 * Form per creazione/modifica configurazione scatola.
 * Include selezione componenti con quantità.
 */
export default function ConfigurazioneForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isEditing = !!id

  // Form state
  const [formData, setFormData] = useState({
    codice: '',
    nome: '',
    descrizione: '',
    struttura_imballo: 'due_pezzi',
    larghezza_interna_cm: '',
    altezza_interna_cm: '',
    profondita_interna_cm: '',
    attivo: true,
  })
  const [componentiSelezionati, setComponentiSelezionati] = useState([])
  const [errors, setErrors] = useState({})
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  // Query dettaglio configurazione (se editing)
  const { data: configData, isLoading: isLoadingConfig } = useQuery({
    queryKey: ['configurazione-scatola', id],
    queryFn: () => configurazioniApi.getById(id),
    enabled: isEditing,
  })

  // Query componenti disponibili (tutti, oltre la paginazione DRF di 20)
  const { data: componentiData } = useQuery({
    queryKey: ['componenti-scatola', { attivo: true, all: true }],
    queryFn: () => fetchAllPaginatedResults(
      (params) => componentiApi.getAll(params),
      { attivo: true }
    ),
  })

  const componentiDisponibili = Array.isArray(componentiData) ? componentiData : []

  // Stato popover combobox per aggiungi componente
  const [comboOpen, setComboOpen] = useState(false)
  const [comboSearch, setComboSearch] = useState('')

  // Reset search quando chiudi il popover
  useEffect(() => {
    if (!comboOpen) setComboSearch('')
  }, [comboOpen])

  // Popola form se editing
  useEffect(() => {
    if (configData?.data) {
      const config = configData.data
      setFormData({
        codice: config.codice || '',
        nome: config.nome || '',
        descrizione: config.descrizione || '',
        struttura_imballo: config.struttura_imballo || 'due_pezzi',
        larghezza_interna_cm: config.larghezza_interna_cm?.toString() || '',
        altezza_interna_cm: config.altezza_interna_cm?.toString() || '',
        profondita_interna_cm: config.profondita_interna_cm?.toString() || '',
        attivo: config.attivo ?? true,
      })
      if (config.componenti_dettaglio) {
        setComponentiSelezionati(config.componenti_dettaglio.map(c => ({
          componente: c.componente,
          componente_nome: c.componente_nome,
          componente_codice: c.componente_codice || '',
          componente_tipo: c.componente_tipo,
          costo_unitario_cents: c.costo_unitario_cents,
          quantita: c.quantita,
        })))
      }
    }
  }, [configData])

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data) => configurazioniApi.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['configurazioni-scatola'] })
      toast.success('Configurazione creata con successo')
      navigate(`/box/configurazioni/${response.data.id}/modifica`)
    },
    onError: (error) => {
      const errorData = error.response?.data
      if (errorData) setErrors(errorData)
      toast.error('Errore durante la creazione')
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data) => configurazioniApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configurazioni-scatola'] })
      queryClient.invalidateQueries({ queryKey: ['configurazione-scatola', id] })
      toast.success('Configurazione aggiornata con successo')
      navigate('/box/configurazioni')
    },
    onError: (error) => {
      const errorData = error.response?.data
      if (errorData) setErrors(errorData)
      toast.error('Errore durante l\'aggiornamento')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => configurazioniApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configurazioni-scatola'] })
      queryClient.invalidateQueries({ queryKey: ['configurazione-scatola', id] })
      toast.success('Configurazione eliminata con successo')
      navigate('/box/configurazioni')
    },
    onError: () => {
      toast.error('Errore durante l\'eliminazione')
    },
  })

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }))
  }

  // Gestione componenti
  const addComponente = (componenteId) => {
    const componente = componentiDisponibili.find(c => c.id === parseInt(componenteId))
    if (!componente) return
    
    // Verifica se già selezionato
    if (componentiSelezionati.find(c => c.componente === componente.id)) return
    
    // Validazione per tipo: fondo e coperchio max 1
    const tipo = componente.tipo?.toLowerCase()
    if (tipo === 'fondo' && componentiSelezionati.find(c => c.componente_tipo?.toLowerCase() === 'fondo')) {
      toast.error('Puoi selezionare solo 1 fondo')
      return
    }
    if (tipo === 'coperchio' && componentiSelezionati.find(c => c.componente_tipo?.toLowerCase() === 'coperchio')) {
      toast.error('Puoi selezionare solo 1 coperchio')
      return
    }
    
    setComponentiSelezionati([...componentiSelezionati, {
      componente: componente.id,
      componente_nome: componente.nome,
      componente_codice: componente.codice || '',
      componente_tipo: componente.tipo_display || componente.tipo,
      tipo_raw: tipo,
      costo_unitario_cents: componente.costo_cents,
      quantita: 1,
    }])
  }

  const updateQuantita = (componenteId, quantita) => {
    setComponentiSelezionati(componentiSelezionati.map(c =>
      c.componente === componenteId ? { ...c, quantita: Math.max(1, parseInt(quantita) || 1) } : c
    ))
  }

  const removeComponente = (componenteId) => {
    setComponentiSelezionati(componentiSelezionati.filter(c => c.componente !== componenteId))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    
    // Validazione con utility centralizzata
    const newErrors = validateConfigurazione(formData)
    
    if (componentiSelezionati.length === 0) {
      toast.error('Devi selezionare almeno 1 componente')
      return
    }

    // Regola condizionale: solo "due pezzi" richiede fondo + coperchio.
    if (formData.struttura_imballo === 'due_pezzi') {
      const hasFondo = componentiSelezionati.some(c => c.componente_tipo?.toLowerCase() === 'fondo')
      const hasCoperchio = componentiSelezionati.some(c => c.componente_tipo?.toLowerCase() === 'coperchio')

      if (!hasFondo) {
        toast.error('Per struttura due pezzi devi selezionare almeno 1 fondo')
        return
      }
      if (!hasCoperchio) {
        toast.error('Per struttura due pezzi devi selezionare almeno 1 coperchio')
        return
      }
    }
    
    if (hasErrors(newErrors)) {
      setErrors(newErrors)
      return
    }

    const data = {
      nome: formData.nome.trim(),
      descrizione: formData.descrizione.trim(),
      struttura_imballo: formData.struttura_imballo,
      larghezza_interna_cm: formData.larghezza_interna_cm ? parseFloat(formData.larghezza_interna_cm) : null,
      altezza_interna_cm: formData.altezza_interna_cm ? parseFloat(formData.altezza_interna_cm) : null,
      profondita_interna_cm: formData.profondita_interna_cm ? parseFloat(formData.profondita_interna_cm) : null,
      attivo: formData.attivo,
      componenti_input: componentiSelezionati.map(c => ({
        componente: c.componente,
        quantita: c.quantita,
      })),
    }

    if (isEditing) {
      updateMutation.mutate(data)
    } else {
      createMutation.mutate(data)
    }
  }

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(cents / 100)
  }

  // Calcolo costo totale
  const costoTotale = componentiSelezionati.reduce(
    (acc, c) => acc + (c.costo_unitario_cents || 0) * c.quantita,
    0
  )

  const isPending = createMutation.isPending || updateMutation.isPending

  if (isEditing && isLoadingConfig) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/box/configurazioni">
            <ArrowLeftIcon className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {isEditing ? 'Modifica Configurazione' : 'Nuova Configurazione'}
          </h1>
          <p className="text-muted-foreground">
            {isEditing ? 'Modifica i dettagli della configurazione' : 'Crea una nuova configurazione scatola'}
          </p>
        </div>
        </div>
        {isEditing && (
          <Button
            type="button"
            variant="destructive"
            onClick={() => setDeleteDialogOpen(true)}
          >
            <TrashIcon className="h-4 w-4 mr-2" />
            Elimina
          </Button>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid gap-6 md:grid-cols-2">
          {/* Colonna sinistra - Info base */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Informazioni Base</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="codice">Codice (automatico)</Label>
                    <Input
                      id="codice"
                      value={formData.codice}
                      disabled
                      placeholder={formData.codice ? '' : 'Verrà generato automaticamente'}
                    />
                    <p className="text-xs text-muted-foreground">
                      Generato automaticamente dal nome al salvataggio.
                    </p>
                  </div>
                  <div className="flex items-center justify-end gap-2 pt-6">
                    <Label htmlFor="attivo">Attiva</Label>
                    <Switch
                      id="attivo"
                      checked={formData.attivo}
                      onCheckedChange={(checked) => handleChange('attivo', checked)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="nome">Nome *</Label>
                  <Input
                    id="nome"
                    value={formData.nome}
                    onChange={(e) => handleChange('nome', e.target.value)}
                    placeholder="Scatola Natale Grande"
                    className={errors.nome ? 'border-red-500' : ''}
                  />
                  {errors.nome && <p className="text-sm text-red-500">{errors.nome}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="descrizione">Descrizione</Label>
                  <Textarea
                    id="descrizione"
                    value={formData.descrizione}
                    onChange={(e) => handleChange('descrizione', e.target.value)}
                    placeholder="Descrizione opzionale..."
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="struttura_imballo">Struttura Imballo *</Label>
                  <Select
                    value={formData.struttura_imballo}
                    onValueChange={(value) => handleChange('struttura_imballo', value)}
                  >
                    <SelectTrigger id="struttura_imballo">
                      <SelectValue placeholder="Seleziona struttura" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="due_pezzi">Due pezzi (fondo + coperchio)</SelectItem>
                      <SelectItem value="monoblocco">Monoblocco (pezzo unico)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {formData.struttura_imballo === 'due_pezzi'
                      ? 'Richiede almeno un fondo e un coperchio.'
                      : 'Richiede almeno un componente, senza obbligo fondo/coperchio.'}
                  </p>
                  {errors.struttura_imballo && (
                    <p className="text-sm text-red-500">{errors.struttura_imballo}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Dimensioni Interne</CardTitle>
                <CardDescription>Spazio utile interno della scatola (in cm)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="larghezza">Larghezza (cm)</Label>
                    <Input
                      id="larghezza"
                      type="number"
                      step="0.1"
                      value={formData.larghezza_interna_cm}
                      onChange={(e) => handleChange('larghezza_interna_cm', e.target.value)}
                      placeholder="28"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="altezza">Lunghezza (cm)</Label>
                    <Input
                      id="altezza"
                      type="number"
                      step="0.1"
                      value={formData.altezza_interna_cm}
                      onChange={(e) => handleChange('altezza_interna_cm', e.target.value)}
                      placeholder="18"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="profondita">Altezza (cm)</Label>
                    <Input
                      id="profondita"
                      type="number"
                      step="0.1"
                      value={formData.profondita_interna_cm}
                      onChange={(e) => handleChange('profondita_interna_cm', e.target.value)}
                      placeholder="8"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Colonna destra - Componenti */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Componenti</CardTitle>
                    <CardDescription>Seleziona i componenti della scatola</CardDescription>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Costo totale</p>
                    <p className="text-xl font-bold">{formatCurrency(costoTotale)}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Aggiungi componente — combobox con autocomplete */}
                <Popover open={comboOpen} onOpenChange={setComboOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      role="combobox"
                      aria-expanded={comboOpen}
                      className="w-full justify-between"
                    >
                      Aggiungi componente...
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command>
                      <CommandInput
                        placeholder="Cerca per nome, codice o tipo..."
                        value={comboSearch}
                        onValueChange={setComboSearch}
                      />
                      <CommandList>
                        {(() => {
                          const filtrati = componentiDisponibili
                            .filter(c => !componentiSelezionati.find(s => s.componente === c.id))
                            .filter(c => {
                              const q = comboSearch.trim().toLowerCase()
                              if (!q) return true
                              const haystack = [
                                c.nome,
                                c.codice,
                                c.tipo_display,
                                c.tipo,
                              ]
                                .filter(Boolean)
                                .join(' ')
                                .toLowerCase()
                              return haystack.includes(q)
                            })

                          if (filtrati.length === 0) {
                            return <CommandEmpty>Nessun componente trovato</CommandEmpty>
                          }

                          return (
                            <CommandGroup>
                              {filtrati.map((componente) => (
                                <CommandItem
                                  key={componente.id}
                                  onSelect={() => {
                                    addComponente(componente.id.toString())
                                    setComboOpen(false)
                                  }}
                                >
                                  <span className="font-medium">{componente.nome}</span>
                                  <span className="ml-2 text-xs text-muted-foreground">
                                    ({componente.tipo_display || componente.tipo}) · {formatCurrency(componente.costo_cents)}
                                  </span>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          )
                        })()}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {errors.componenti_input && (
                  <p className="text-sm text-red-500">
                    {Array.isArray(errors.componenti_input)
                      ? errors.componenti_input.join(' ')
                      : errors.componenti_input}
                  </p>
                )}

                <Separator />

                {/* Lista componenti selezionati */}
                {componentiSelezionati.length > 0 ? (
                  <div className="readable-rows overflow-hidden rounded-lg border">
                    {componentiSelezionati.map((comp) => (
                      <div
                        key={comp.componente}
                        className="readable-row flex flex-wrap items-center gap-3 p-3"
                      >
                        <div className="flex-1">
                          <p className="font-medium">{comp.componente_nome}</p>
                          {comp.componente_codice && (
                            <p className="text-xs text-muted-foreground font-mono">{comp.componente_codice}</p>
                          )}
                          <p className="text-sm text-muted-foreground">
                            {comp.componente_tipo} - {formatCurrency(comp.costo_unitario_cents)} cad.
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="1"
                            value={comp.quantita}
                            onChange={(e) => updateQuantita(comp.componente, e.target.value)}
                            className="w-20 text-center"
                          />
                          <span className="text-sm text-muted-foreground w-20 text-right">
                            = {formatCurrency(comp.costo_unitario_cents * comp.quantita)}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="text-red-600"
                            onClick={() => removeComponente(comp.componente)}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Nessun componente aggiunto</p>
                    <p className="text-sm">Usa il menu sopra per aggiungere componenti</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Azioni */}
            <div className="flex justify-end gap-4">
              <Button type="button" variant="outline" asChild>
                <Link to="/box/configurazioni">Annulla</Link>
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? 'Salvataggio...' : (isEditing ? 'Salva Modifiche' : 'Crea Configurazione')}
              </Button>
            </div>
          </div>
        </div>
      </form>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Elimina Configurazione</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare la configurazione &quot;{formData.nome}&quot;?
              Questa azione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Annulla
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Eliminazione...' : 'Elimina'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
